/**
 * Aiuti per il tabellone delle assegnazioni: cosa salvare dopo una proposta,
 * chi si puo' confermare, quando un'aula e' "in rosso", chi e' rimasto senza
 * posto. Le regole vere (proposta, completezza) sono in @eventi/assegna.ts.
 */

import { assegnazioneCompleta, type ConteggioSessione, type SenzaPosto } from '@eventi/assegna.ts';
import { sessionePerCodice, sessioniDelPercorso } from '@eventi/regole.ts';
import type { Percorso, ProgrammaEvento } from '@eventi/tipi.ts';
import { nomeAula } from './programma';
import type { PartecipanteEvento } from './tipi';

/** Lo stesso percorso scritto con le chiavi in un ordine qualsiasi. */
function chiave(percorso: Percorso | null | undefined): string {
  if (!percorso) return '';
  return Object.keys(percorso)
    .sort()
    .map((g) => {
      const delGiorno = percorso[g] ?? {};
      return `${g}:${Object.keys(delGiorno).sort().map((t) => `${t}=${delGiorno[t]}`).join(',')}`;
    })
    .join('|');
}

export function stessoPercorso(a: Percorso | null | undefined, b: Percorso | null | undefined): boolean {
  return chiave(a) === chiave(b);
}

export interface BozzaDaSalvare {
  id: string;
  assegnazione: Percorso;
}

/**
 * Le bozze della proposta che cambiano davvero qualcosa. I confermati non ci
 * sono mai (la proposta non li tocca, e qui si ricontrolla); chi ha gia' la
 * stessa bozza si salta, cosi' si scrive solo il necessario.
 */
export function bozzeDaSalvare(bozze: Map<string, Percorso>, partecipanti: PartecipanteEvento[]): BozzaDaSalvare[] {
  const perId = new Map(partecipanti.map((p) => [p.id, p]));
  const righe: BozzaDaSalvare[] = [];
  for (const [id, assegnazione] of bozze) {
    const p = perId.get(id);
    if (!p || p.assegnazione_stato === 'confermata') continue;
    if (p.assegnazione_stato === 'bozza' && stessoPercorso(p.assegnazione, assegnazione)) continue;
    righe.push({ id, assegnazione });
  }
  return righe;
}

/** Bozze con almeno un turno assegnato: si possono confermare. */
export function confermabili(partecipanti: PartecipanteEvento[]): PartecipanteEvento[] {
  return partecipanti.filter(
    (p) => p.assegnazione_stato === 'bozza' && sessioniDelPercorso(p.assegnazione).length > 0,
  );
}

/** Bozze complete (tutti i turni di tutti i giorni): "Conferma tutti i pronti". */
export function prontiDaConfermare(programma: ProgrammaEvento, partecipanti: PartecipanteEvento[]): PartecipanteEvento[] {
  return partecipanti.filter(
    (p) => p.assegnazione_stato === 'bozza' && assegnazioneCompleta(programma, p.assegnazione),
  );
}

export type LivelloSessione = 'oltre' | 'pieno' | 'ok' | 'senza_limite';

/**
 * Il colore di una sessione nel tabellone. Rosso ('oltre') se gli assegnati o
 * i richiesti superano i posti; 'pieno' se gli assegnati li hanno raggiunti.
 */
export function livelloSessione(c: ConteggioSessione): LivelloSessione {
  if (c.posti === null) return 'senza_limite';
  if (c.assegnati > c.posti || c.richiesti > c.posti) return 'oltre';
  if (c.assegnati === c.posti) return 'pieno';
  return 'ok';
}

/** Perche' una sessione e' rossa, in parole. */
export function motivoLivello(c: ConteggioSessione): string {
  if (c.posti === null) return 'Aula senza limite di posti';
  const motivi: string[] = [];
  if (c.assegnati > c.posti) motivi.push(`${c.assegnati - c.posti} assegnati oltre i posti`);
  if (c.richiesti > c.posti) motivi.push(`${c.richiesti - c.posti} richieste piu' dei posti`);
  if (motivi.length === 0) return c.assegnati === c.posti ? 'Aula piena' : `${c.posti - c.assegnati} posti liberi`;
  return motivi.join(' · ');
}

export interface RigaSenzaPosto {
  partecipanteId: string;
  nome: string;
  email: string;
  giorno: string;
  giornoNome: string;
  turno: number;
  aula: string;
  titolo: string;
}

/** Chi e' rimasto senza posto, con nomi e titoli leggibili, in ordine di nome. */
export function righeSenzaPosto(
  programma: ProgrammaEvento,
  senzaPosto: SenzaPosto[],
  partecipanti: PartecipanteEvento[],
): RigaSenzaPosto[] {
  const perId = new Map(partecipanti.map((p) => [p.id, p]));
  const perCodice = sessionePerCodice(programma);
  const giorni = new Map(programma.giorni.map((g) => [g.codice, g.nome]));
  return senzaPosto
    .map((x) => {
      const p = perId.get(x.partecipanteId);
      const s = perCodice.get(x.sessione);
      return {
        partecipanteId: x.partecipanteId,
        nome: p?.nome ?? 'Partecipante',
        email: p?.email ?? '',
        giorno: x.giorno,
        giornoNome: giorni.get(x.giorno) ?? x.giorno,
        turno: x.turno,
        aula: s ? nomeAula(programma, s.aula) : '',
        titolo: s?.titolo ?? x.sessione,
      };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome, 'it') || a.giornoNome.localeCompare(b.giornoNome) || a.turno - b.turno);
}

/** Toglie dalle righe "da sistemare" i turni che nel nuovo percorso della persona sono stati riempiti. */
export function senzaPostoDopoModifica(righe: RigaSenzaPosto[], partecipanteId: string, percorso: Percorso): RigaSenzaPosto[] {
  return righe.filter((r) => r.partecipanteId !== partecipanteId || !percorso[r.giorno]?.[String(r.turno)]);
}

/** Chi ha chiesto o ha assegnata una sessione (per filtrare l'elenco dal tabellone). */
export function coinvoltiNellaSessione(partecipanti: PartecipanteEvento[], codice: string): PartecipanteEvento[] {
  return partecipanti.filter(
    (p) => sessioniDelPercorso(p.richiesta).includes(codice) || sessioniDelPercorso(p.assegnazione).includes(codice),
  );
}
